<template>
  <Loading
    :loading="isLoading"
    :loading-text="loadingText"
    background="none-background"
    class="encounter-loading"
  >
    <div
      class="encounter-note-container"
      :aria-busy="isLoading"
    >
      <div class="encounter-content-wrapper">
        <div class="encounter-content">
          <PatientSummary
            :first-name="patientFirstName"
            :last-name="patientLastName"
            :bio-gender="patientBioGender"
            :dob="patientDob"
            :current-visit="appointmentType"
            :visit-date="appointmentDate"
            :past-notes="pastNotes"
            @before-navigate="handleBeforeNavigate"
          />

          <EncounterMetaBar
            :title="encounterTitle"
            :date="appointmentDate"
            :appointment-type="appointmentType"
            :note-id="route.params.noteId"
            :is-draft="isDraft"
            :is-edit-locked="isEditLocked"
            @update:title="handleTitleUpdate"
            @update:date="appointmentDate = $event"
            @update:appointmentType="appointmentType = $event"
            :download-handler="handleDownload"
            @deleted="handleDeleted"
            @complete="handleComplete"
          />

          <EncounterEditor
            ref="editorRef"
            v-model="noteContent"
            placeholder="Type here to reply ..."
            :save-status="saveStatus"
            :encounter-note-id="route.params.noteId"
            :patient-id="patientId"
            :is-draft="isDraft"
            :is-edit-locked="isEditLocked"
            :has-recording="hasRecording"
            :recordings="recordings"
            :patient-variables="patientVariables"
            :patient-match-data="patientMatchData"
            @save="saveNote"
            @slashCommand="handleSlashCommand"
            @editTask="handleEditTask"
            @deleteTask="handleDeleteTask"
            @editAppointment="handleEditAppointment"
            @cancelAppointment="handleCancelAppointment"
            @previewDocument="handlePreviewDocument"
            @deleteDocument="handleDeleteDocument"
            @seeOrderDetails="handleSeeOrderDetails"
            @seeFormDetails="handleSeeFormDetails"
          />
        </div>
      </div>

      <aside class="encounter-sidebar">
        <div class="pegboard-wrapper">
          <Pegboard
            class="flex-1"
            :patient="patientId"
            :roomId="null"
            :isLeftColumnCollapsed="false"
          />
        </div>
      </aside>
    </div>

    <!-- Slash Command Dialogs -->
    <ScheduleDetail
      :visible="showCreateAppointmentDialog"
      :active-tab="scheduleDetailActiveTab"
      :hide-switch-tab="true"
      @close="handleAppointmentDialogClose"
      @createTask="handleCreateTask"
      @createEvent="handleCreateEvent"
      @updateTask="handleUpdateTask"
      @updateEvent="handleUpdateEvent"
    />

    <!-- Signature Modal -->
    <SetSignatureModal
      v-model="showSignatureModal"
      :customer-id="customerId"
      :note-id="route.params.noteId"
      @sign="handleSignature"
    />

    <!-- Signed Success Modal -->
    <SignedSuccessModal
      v-model="showSignedSuccessModal"
      @done="handleSignedDone"
    />

    <!-- Delete Task Dialog -->
    <DeleteTaskDialog
      :visible="showDeleteTaskDialog"
      @close="handleDeleteTaskDialogClose"
      @delete="handleDeleteTaskConfirm"
    />

    <!-- Cancel Appointment Dialog -->
    <CancelAppointmentDialog
      :visible="showCancelAppointmentDialog"
      @close="handleCancelAppointmentDialogClose"
      @cancel="handleCancelAppointmentConfirm"
    />

    <!-- Delete Document Dialog -->
    <DeleteDocumentDialog
      :visible="showDeleteDocumentDialog"
      @close="handleDeleteDocumentDialogClose"
      @delete="handleDeleteDocumentConfirm"
    />

    <!-- New Order Dialog -->
    <NewOrderDialog
      :visible="showNewOrderDialog"
      :patient-id="Number(patientId) || null"
      :customer-id="Number(customerId) || null"
      :is-clinic="userInfo?.user_roles?.includes('clinic') || false"
      :is-us-customer="(userInfo?.customer_country || '').toLowerCase() === 'us'"
      :clinic-id="Number(clinicId) || null"
      :initial-order-items="initialOrderItems"
      :initial-is-dbs-version="initialOrderItems.length > 0 ? false : null"
      @close="handleNewOrderDialogClose"
      @send="handleOrderSend"
    />

    <!-- Send Document Dialog -->
    <SendDocumentDialog
      :visible="showSendDocumentDialog"
      :roomId="null"
      :clinicId="clinicId"
      :patientId="patientId"
      source="encounter"
      @close="showSendDocumentDialog = false"
      @send="handleSendDocuments"
    />
  </Loading>
</template>

<script setup>
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import moment from "moment";
import { generateNotePdf } from "./utils/generateNotePdf";
import { getEncounterNoteApi } from "@/api/encounterNotes";
import { useEncounterNoteInit } from "./composables/useEncounterNoteInit";
import { useEditLock } from "./composables/useEditLock";
import { useAutoSave } from "./composables/useAutoSave";
import { useActionCards } from "./composables/useActionCards";
import PatientSummary from "./components/PatientSummary.vue";
import EncounterMetaBar from "./components/EncounterMetaBar.vue";
import EncounterEditor from "./components/EncounterEditor.vue";
import Pegboard from "../my-patients/Pegboard.vue";
import Loading from "@/NewLibrary/Loading/Loading.vue";
import ScheduleDetail from "../my-calendar/sections/schedule/ScheduleDetail.vue";
import DeleteTaskDialog from "../my-calendar/sections/task/DeleteTaskDialog.vue";
import CancelAppointmentDialog from "./components/CancelAppointmentDialog.vue";
import DeleteDocumentDialog from "./components/DeleteDocumentDialog.vue";
import SetSignatureModal from "./components/SetSignatureModal.vue";
import SignedSuccessModal from "./components/SignedSuccessModal.vue";
import SendDocumentDialog from "../MyPatient-demo/SendDocumentDialog.vue";
import NewOrderDialog from "../MyPatient-demo/NewOrderDialog.vue";

const route = useRoute();
const router = useRouter();
const store = useStore();

const patientId = computed(() => String(route.params.patientId || ""));
const noteId = computed(() => route.params.noteId || "");

const appointmentDate = ref(moment().format("YYYY-MM-DD"));
const appointmentType = ref("Initial Consultation");
const encounterTitle = ref(`Encounter Note - ${moment(appointmentDate.value).format("MMM DD, YYYY")}`);
const noteContent = ref("");
const contentVersion = ref(1);
const isTitleManuallyEdited = ref(false);
const signedAt = ref(null);
const saveStatus = ref(""); // shared between init and auto-save
const lastLoadedContent = ref(""); // shared between init and auto-save
const lastSavedMetadata = ref(null); // tracks last saved metadata to prevent redundant saves

const isDraft = computed(() => signedAt.value === null || signedAt.value === undefined);

// --- Initialization ---
const {
  patientRecord,
  pastNotes,
  recordings,
  hasRecording,
  patientVariables,
  patientMatchData,
  isLoadingPatient,
  isLoadingCalendar,
  isInitializing,
} = useEncounterNoteInit({
  route,
  router,
  store,
  patientId,
  appointmentDate,
  appointmentType,
  encounterTitle,
  noteContent,
  contentVersion,
  saveStatus,
  isTitleManuallyEdited,
  signedAt,
  lastLoadedContent,
});

// --- Refresh note data when page becomes active ---
const refreshNoteData = async () => {
  const currentNoteId = route.params.noteId;
  if (!currentNoteId) return;
  try {
    const note = await getEncounterNoteApi(currentNoteId);
    if (!note) return;
    if (note.title) {
      encounterTitle.value = note.title;
      isTitleManuallyEdited.value = true;
    }
    if (note.service_date) {
      appointmentDate.value = moment(note.service_date).format("YYYY-MM-DD");
    }
    if (note.appointment_type) {
      appointmentType.value = note.appointment_type;
    }
    noteContent.value = note.content || "";
    if (lastLoadedContent) {
      lastLoadedContent.value = note.content || "";
    }
    if (lastSavedMetadata) {
      lastSavedMetadata.value = {
        title: note.title || encounterTitle.value,
        date: appointmentDate.value,
        appointmentType: note.appointment_type || appointmentType.value,
      };
    }
    contentVersion.value = note.content_version || contentVersion.value;
    if (signedAt) {
      signedAt.value = note.signed_at || null;
    }
  } catch (error) {
    console.error("Failed to refresh note data:", error);
  }
};

// --- Edit Lock ---
const { isEditLocked, isCheckingLock, lockedAt, releaseLock } = useEditLock({
  route,
  store,
  isInitializing,
  isDraft,
  onPageActive: refreshNoteData,
});

// --- Auto-save ---
const handleLockConflict = () => {
  isEditLocked.value = true;
};

const { saveNote } = useAutoSave({
  noteId,
  isDraft,
  noteContent,
  encounterTitle,
  appointmentDate,
  appointmentType,
  contentVersion,
  isInitializing,
  saveStatus,
  lastLoadedContent,
  lockedAt,
  onLockConflict: handleLockConflict,
});

// --- Loading ---
const isLoading = computed(() => isLoadingPatient.value || isLoadingCalendar.value || isCheckingLock.value);
const loadingText = computed(() => {
  if (isLoadingCalendar.value) return "Loading calendar data…";
  if (isLoadingPatient.value) return "Loading patient…";
  if (isCheckingLock.value) return "Checking edit access…";
  return "";
});

// --- Patient info ---
const patientFirstName = computed(() => patientRecord.value?.patient_first_name || "");
const patientLastName = computed(() => patientRecord.value?.patient_last_name || "");
const patientBioGender = computed(() => patientRecord.value?.patient_gender || "N/A");
const patientDob = computed(() => patientRecord.value?.patient_birthdate || "N/A");

const userInfo = computed(() => store.state.login?.userInfo);

const customerId = computed(() => {
  return userInfo.value?.customer_id ? String(userInfo.value.customer_id) : '';
});

const handleTitleUpdate = (newTitle) => {
  encounterTitle.value = newTitle;
  isTitleManuallyEdited.value = true;
};

const handleDeleted = () => {
  router.replace({ path: '/encounter-notes', query: { patientId: patientId.value } });
};

// --- Signature ---
const showSignatureModal = ref(false);
const showSignedSuccessModal = ref(false);

const handleComplete = () => {
  showSignatureModal.value = true;
};

const handleSignature = ({ signResult }) => {
  signedAt.value = signResult?.signed_at || new Date().toISOString();
  showSignedSuccessModal.value = true;
};

const handleSignedDone = () => {};

const handleDownload = async () => {
  try {
    const noteData = {
      content: noteContent.value,
      title: encounterTitle.value,
      service_date: appointmentDate.value,
      signed_at: signedAt.value,
    };
    await generateNotePdf(patientId.value, store, noteData);
  } catch (error) {
    console.error('Failed to generate PDF:', error);
    const { notify } = await import('@/utils/notify');
    notify({
      type: 'error',
      title: 'Download Failed',
      message: error.message || 'Failed to generate PDF.',
      duration: 5000,
    });
  }
};

// --- Action Cards ---
const editorRef = ref(null);

const {
  showCreateAppointmentDialog,
  showSendDocumentDialog,
  showNewOrderDialog,
  showDeleteTaskDialog,
  showCancelAppointmentDialog,
  showDeleteDocumentDialog,
  scheduleDetailActiveTab,
  clinicId,
  handleSlashCommand,
  handleAppointmentDialogClose,
  handleCreateTask,
  handleUpdateTask,
  handleEditTask,
  handleDeleteTask,
  handleDeleteTaskDialogClose,
  handleDeleteTaskConfirm,
  handleCreateEvent,
  handleUpdateEvent,
  handleEditAppointment,
  handleCancelAppointment,
  handleCancelAppointmentDialogClose,
  handleCancelAppointmentConfirm,
  initialOrderItems,
  handleOrderSend,
  handleSeeOrderDetails,
  handleSeeFormDetails,
  handleSendDocuments,
  handlePreviewDocument,
  handleDeleteDocument,
  handleDeleteDocumentDialogClose,
  handleDeleteDocumentConfirm,
} = useActionCards({
  editorRef,
  patientId,
  patientRecord,
  isLoadingCalendar,
});

const handleNewOrderDialogClose = () => {
  showNewOrderDialog.value = false;
  initialOrderItems.value = [];
};

// --- Navigation ---
const handleBeforeNavigate = async ({ navigate }) => {
  try {
    await saveNote();
    if (editorRef.value?.waitForPendingUploads) {
      await editorRef.value.waitForPendingUploads();
    }
    await releaseLock();
    navigate();
  } catch (error) {
    console.error("Failed to save before navigation:", error);
    navigate();
  }
};
</script>

<style scoped>
.encounter-note-container {
  --sidebar-width: clamp(292px, 25vw, 400px);
  position: relative;
  width: 100%;
  box-sizing: border-box;
  height: calc(100vh - var(--nav-height) - 3px);
  background-color: white;
  overflow: hidden;
}

.encounter-content-wrapper {
  width: calc(100% - var(--sidebar-width));
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.encounter-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.encounter-content {
  flex: 1;
  width: 100%;
  min-width: 0;
}

.encounter-sidebar {
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 0;
  width: var(--sidebar-width);
  padding: 2rem 1rem 1rem;
  background-color: #004879;
  box-shadow: 0px 2px 15px rgba(74, 74, 74, 0.25);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-color: transparent transparent;
  z-index: 10;
}

.encounter-sidebar:hover {
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.pegboard-wrapper {
  width: 100%;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  box-sizing: border-box;
}

.encounter-sidebar :deep(.peg-board-content) {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  flex: 1;
  max-width: 100%;
  box-sizing: border-box;
}
</style>
